import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Mail, CheckCircle, Eye, MousePointer, AlertTriangle, XCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format } from 'date-fns';

type EmailStatus = 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed' | 'complained';

interface EmailStatusBadgeProps {
  status: EmailStatus;
  sentAt?: string | null;
  deliveredAt?: string | null;
  openedAt?: string | null;
  clickedAt?: string | null;
  bouncedAt?: string | null;
  openCount?: number;
  clickCount?: number;
  bounceReason?: string | null;
  showDetails?: boolean;
}

const statusConfig: Record<EmailStatus, { 
  label: string; 
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: React.ElementType;
  className: string;
}> = {
  sent: {
    label: 'Sent',
    variant: 'secondary',
    icon: Mail,
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  delivered: {
    label: 'Delivered',
    variant: 'secondary',
    icon: CheckCircle,
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  opened: {
    label: 'Opened',
    variant: 'secondary',
    icon: Eye,
    className: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  clicked: {
    label: 'Clicked',
    variant: 'secondary',
    icon: MousePointer,
    className: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  },
  bounced: {
    label: 'Bounced',
    variant: 'destructive',
    icon: AlertTriangle,
    className: 'bg-orange-100 text-orange-800 border-orange-200',
  },
  failed: {
    label: 'Failed',
    variant: 'destructive',
    icon: XCircle,
    className: 'bg-red-100 text-red-800 border-red-200',
  },
  complained: {
    label: 'Spam',
    variant: 'destructive',
    icon: AlertTriangle,
    className: 'bg-red-100 text-red-800 border-red-200',
  },
};

const EmailStatusBadge: React.FC<EmailStatusBadgeProps> = ({
  status,
  sentAt,
  deliveredAt,
  openedAt,
  clickedAt,
  bouncedAt,
  openCount = 0,
  clickCount = 0,
  bounceReason,
  showDetails = false,
}) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  const getTooltipContent = () => {
    const lines: string[] = [];
    
    if (sentAt) {
      lines.push(`Sent: ${format(new Date(sentAt), 'dd MMMM yyyy HH:mm')}`);
    }
    if (deliveredAt) {
      lines.push(`Delivered: ${format(new Date(deliveredAt), 'dd MMMM yyyy HH:mm')}`);
    }
    if (openedAt) {
      lines.push(`First opened: ${format(new Date(openedAt), 'dd MMMM yyyy HH:mm')}`);
      if (openCount > 1) {
        lines.push(`Total opens: ${openCount}`);
      }
    }
    if (clickedAt) {
      lines.push(`First clicked: ${format(new Date(clickedAt), 'dd MMMM yyyy HH:mm')}`);
      if (clickCount > 1) {
        lines.push(`Total clicks: ${clickCount}`);
      }
    }
    if (bouncedAt && bounceReason) {
      lines.push(`Bounced: ${bounceReason}`);
    }
    
    return lines.length > 0 ? lines.join('\n') : config.label;
  };

  const badge = (
    <Badge 
      variant={config.variant}
      className={`${config.className} gap-1 font-medium text-[11px] sm:text-xs px-1.5 py-0.5 shrink-0 whitespace-nowrap`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {config.label}
      {showDetails && openCount > 0 && status === 'opened' && (
        <span className="ml-0.5">({openCount})</span>
      )}
      {showDetails && clickCount > 0 && status === 'clicked' && (
        <span className="ml-0.5">({clickCount})</span>
      )}
    </Badge>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent>
          <pre className="text-xs whitespace-pre-wrap">{getTooltipContent()}</pre>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EmailStatusBadge;
